import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import { Button } from '@/components/ui/button';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useSoldProductFilterOptions } from '@/hooks/useSoldProductFilterOptions';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { fetchReportTemplateOptions } from '@/utils/checkoutTemplates';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FILTER_UTM_PANEL_MODAL } from '../constants';

const optionSchema = z.array(
  z.object({ value: z.string(), label: z.string() }),
);

const FilterSchema = z.object({
  products: optionSchema.optional(),
  offers: optionSchema.optional(),
  checkouts: optionSchema.optional(),
  affiliates: optionSchema.optional(),
  coproducers: optionSchema.optional(),
  templates: optionSchema.optional(),
});

export type FilterUtmPanelFormData = z.infer<typeof FilterSchema>;

interface FilterUtmPanelProps {
  filterValues: FilterUtmPanelFormData;
  onFilter: (data: FilterUtmPanelFormData) => void;
}

export default function FilterUtmPanel({
  filterValues,
  onFilter,
}: FilterUtmPanelProps) {
  const { t } = useTranslation(['common', 'sales', 'reports']);
  const { closeModal } = useModalStore();
  const { currency } = useCurrencyStore();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    getValues,
  } = useForm<FilterUtmPanelFormData>({
    shouldUnregister: false,
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      checkouts: filterValues?.checkouts || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      templates: filterValues?.templates || [],
    },
  });

  const {
    productIds,
    productIdsKey,
    offerIds,
    offerIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchCheckoutsOptions,
    fetchAffiliatesOptions,
    fetchCoProducersOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: 'utm-panel',
    enableCheckouts: true,
    enableAffiliates: true,
    enableCoproducers: true,
  });

  const onSubmit = (data: FilterUtmPanelFormData) => {
    onFilter(data);
    closeModal(FILTER_UTM_PANEL_MODAL);
  };

  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t('sales:filters')}</SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6 mb-24">
          <MultiComboBox
            label={t('common:products')}
            placeholder={t('sales:products_placeholder')}
            fetchOptions={fetchProductOptions}
            values={watch('products')}
            onChange={(value) => {
              handleProductsChange(value);
              clearErrors('products');
            }}
            helperText={errors.products?.message}
            queryKey="sold-products"
            returnFullObject
            cypressName="multicombobox-products"
            selectAllOptions
            newParams={[currency]}
            isEditing
          />

          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t('common:offers')}
            placeholder={t('sales:offer_placeholder')}
            fetchOptions={fetchOfferOptions}
            values={watch('offers')}
            onChange={(values) => {
              handleOffersChange(values);
              clearErrors('offers');
            }}
            helperText={errors.offers?.message}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName="multicombobox-offers"
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
            isEditing
          />

          <MultiComboBox
            key={`checkouts-${productIdsKey}-${offerIdsKey}`}
            label={t('common:select_checkout')}
            placeholder={t('common:select_checkout')}
            fetchOptions={fetchCheckoutsOptions}
            values={watch('checkouts')}
            onChange={(value) => {
              setValue('checkouts', value);
              clearErrors('checkouts');
            }}
            helperText={errors.checkouts?.message}
            queryKey={queryKeys.checkouts}
            returnFullObject
            cypressName="multicombobox-checkouts"
            selectAllOptions
            newParams={[...productIds, ...offerIds]}
            disabled={productIds.length === 0 || offerIds.length === 0}
            isEditing
          />

          <MultiComboBox
            label={t('reports:template')}
            placeholder={t('reports:select_template')}
            fetchOptions={fetchReportTemplateOptions}
            values={watch('templates')}
            onChange={(value) => {
              setValue('templates', value);
              clearErrors('templates');
            }}
            helperText={errors.templates?.message}
            queryKey="report-templates-utm-panel"
            returnFullObject
            cypressName="multicombobox-templates"
            selectAllOptions
            isEditing
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t('sales:affiliate')}
            placeholder={t('sales:affiliate_placeholder')}
            fetchOptions={fetchAffiliatesOptions}
            values={watch('affiliates')}
            onChange={(value) => {
              setValue('affiliates', value);
              clearErrors('affiliates');
            }}
            helperText={errors.affiliates?.message}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName="multicombobox-affiliates"
            selectAllOptions
            newParams={productIds}
          />

          <MultiComboBox
            key={`coproducers-${productIdsKey}`}
            label={t('sales:co_producer')}
            placeholder={t('sales:co_producer_placeholder')}
            fetchOptions={fetchCoProducersOptions}
            values={watch('coproducers')}
            onChange={(value) => {
              setValue('coproducers', value);
              clearErrors('coproducers');
            }}
            helperText={errors.coproducers?.message}
            queryKey={queryKeys.coproducers}
            returnFullObject
            cypressName="multicombobox-coproducers"
            selectAllOptions
            newParams={productIds}
          />
        </section>

        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              onClick={() => closeModal(FILTER_UTM_PANEL_MODAL)}
              type="button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              data-cy="apply-utm-filters"
            >
              {t('sales:apply_filters')}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
